<template>
  <div class="candidate-profile position-relative bg-blue-light-2">
    <!-- Static Header -->
    <div class="candidate-header sticky-top">
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <!-- Left -->
        <div class="d-flex align-items-center">
          <router-link to="/home" class="button bg-light-transparent br-12 text-decoration-none" style="min-width: 26%">
            <i class="fa-solid fa-arrow-left-long"></i>
          </router-link>
          <span class="ml-2 bold">Profile Edit Page</span>
        </div>

        <!-- Right -->
        <div class="d-flex align-items-center gap-2">
          <CButton class="btn btn-primary d-md-none" @click="toggleSidebar">
            <i class="fa-solid fa-bars mr-2"></i> Menu
          </CButton>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="candidate-content-container">
      <!-- Left Panel -->
      <div class="candidate-left-panel" :class="{ 'mobile-open': isSideBarOpen && isMobile }">
        <div class="p-2 bg-white border-radius min-vh-80 sticky-left-panel">
          <div class="list-group list-group-flush">
            <!-- Home Tab for Mobile Sidebar -->
            <div v-if="isMobile && isSideBarOpen" class="list-group-item-action px-0 mb-2" @click="navigateToHome">
              <div class="d-flex align-items-center border-radius hpx-56 p-3 bg-light cursor-pointer-hover-gradient">
                <i class="fa-solid fa-house-chimney mr-3 text-primary"></i>
                <span>Home</span>
              </div>
            </div>

            <div v-for="(i, index) in sectionNamesCandidateProfile" :key="i.id" class="list-group-item-action px-0 mb-2"
              @click="gotoSection(index)">
              <div class="d-flex align-items-center border-radius hpx-56 p-3" :class="[
                activeSection === index
                  ? 'bg-gradient-selected text-white'
                  : 'bg-light cursor-pointer-hover-gradient',
              ]">
                <i :class="[
                  i.icon,
                  'mr-3',
                  activeSection === index ? 'text-white' : 'text-primary',
                ]"></i>
                <span :class="activeSection === index ? 'text-white' : 'text-primary'">{{
                  getSectionTitle(i)
                  }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel -->
      <div class="candidate-right-panel" @click="isSideBarOpen && isMobile ? closeSidebar() : null" ref="rightPanel">
        <div class="card main-content-card shadow-sm">
          <div class="card-body">
            <LoadingOverlay v-if="!isfetch" />
            <div v-if="candidateProfile" style="color: #3f26e7">
              <!-- Profile Sections -->
              <div id="section-0" class="profile-section">
                <ProfessionalInfo ref="professionalInfo" @tooltipHide="tooltipHide" :autoSave="autoSave" />
              </div>
              <div id="section-1" class="profile-section">
                <LanguageQualification :candidateTypeId="candidateProfile.candidate_type_id"
                  @tooltipHide="tooltipHide" />
                <CompetencyExam :candidateTypeId="candidateProfile.candidate_type_id" @tooltipHide="tooltipHide" />
                <NonMedicalInfoTalentFind :candidateTypeId="candidateProfile.candidate_type_id"
                  @tooltipHide="tooltipHide" />
                <LanguageInfo @tooltipHide="tooltipHide" />
              </div>
              <div id="section-2" class="profile-section">
                <MultipleFileUpload ref="multipleFileUpload" :profile="profile" @upload="upload" />
              </div>
              <div id="section-3" class="profile-section">
                <AvailabilityInfo ref="availabilityInfo" :profile="profile" :autoSave="autoSave"
                  @tooltipHide="tooltipHide" />
              </div>
              <div id="section-4" class="profile-section">
                <ContactInfo ref="contactInfo" :profile="profile" :autoSave="autoSave" @tooltipHide="tooltipHide" />
              </div>
              <div id="section-5" class="profile-section">
                <PersonalInfo :profilePhoto="profilePhoto" ref="personalInfo" @upload="photoUpload" :profile="profile"
                  :autoSave="autoSave" @tooltipHide="tooltipHide" />
              </div>

              <div id="section-6" class="profile-section">
                <GDPRStatement ref="gdprStatement" @tooltipHide="tooltipHide" />
                <div class="d-flex justify-content-between mt-4 pt-3 border-top">
                  <CButton class="btn btn-primary button" @click="navigateToHome()">
                    Home
                  </CButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Sidebar Backdrop -->
    <div class="mobile-sidebar-backdrop" v-if="isSideBarOpen && isMobile" @click="closeSidebar"></div>

    <!-- Scroll to Top Button -->
    <button v-show="showGoTop" class="btn-primary position-fixed" style="
        bottom: 55px;
        right: 25px;
        z-index: 1055;
        border-radius: 50%;
        width: 45px;
        height: 45px;
      " @click="scrollToTop">
      <i class="fa fa-arrow-up text-white"></i>
    </button>

    <!-- Toaster -->
    <CToaster v-if="toast" :autohide="toastHide" position="bottom-right">
      <CToast :class="toastClass" :show="true">{{ toastMessage }}</CToast>
    </CToaster>
  </div>
</template>

<script>
import CandidateProfile from "@/pages/CandidateProfile/CandidateProfileMixins.js";

export default {
  mixins: [CandidateProfile],
  data() {
    return {
      isSideBarOpen: false,
      minimize: false,
      isMobile: false,
      showGoTop: false,
      activeSection: 0,
      sectionOffsets: [],
      observer: null,
      autoSave: false,
      scrollTimeout: null,
    };
  },
  computed: {
    candidateType() {
      return this.candidateProfile?.candidate_type?.candidate_type || null;
    },
  },
  methods: {
    toggleSidebar() {
      this.isSideBarOpen = !this.isSideBarOpen;
    },
    closeSidebar() {
      this.isSideBarOpen = false;
    },
    gotoSection(index) {
      this.activeSection = index;
      const el = document.getElementById(`section-${index}`);
      const container = this.$refs.rightPanel;

      if (el && container) {
        const headerOffset = 10;
        const elementPosition = el.offsetTop;
        const offsetPosition = elementPosition - headerOffset;

        container.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }

      this.closeSidebar();
    },
    getSectionTitle(section) {
      return this.isNonClinicAdmin && section.id === 2
        ? "Language"
        : section.title;
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    scrollToTop() {
      const container = this.$refs.rightPanel;
      if (container) {
        container.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    
    handleScroll() {
      const container = this.$refs.rightPanel;
      if (!container) return;

      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        this.showGoTop = container.scrollTop > 200;

        const containerRect = container.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerHeight = containerRect.height;
        const viewportCenter = containerTop + (containerHeight / 2);

        let bestSection = 0;
        let bestDistance = Infinity;

        for (let i = 0; i <= 6; i++) {
          const el = document.getElementById(`section-${i}`);
          if (!el) continue;

          const sectionRect = el.getBoundingClientRect();
          const sectionTop = sectionRect.top;
          const sectionBottom = sectionRect.bottom;
          const sectionHeight = sectionRect.height;

          const isVisible = sectionTop < containerTop + containerHeight && sectionBottom > containerTop;
          
          if (isVisible) {
            const visibleTop = Math.max(sectionTop, containerTop);
            const visibleBottom = Math.min(sectionBottom, containerTop + containerHeight);
            const visibleHeight = visibleBottom - visibleTop;
            const visibilityRatio = visibleHeight / sectionHeight;

            if (visibilityRatio > 0.3) {
              const sectionCenter = sectionTop + (sectionHeight / 2);
              const distance = Math.abs(sectionCenter - viewportCenter);
              
              if (distance < bestDistance) {
                bestDistance = distance;
                bestSection = i;
              }
            }
          }
        }

        if (container.scrollTop < 100) {
          bestSection = 0;
        }

        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
        if (isAtBottom) {
          bestSection = 6;
        }

        this.activeSection = bestSection;
      }, 50); 
    },


    observeSections() {
      const container = this.$refs.rightPanel;
      if (!container) return;

      if (this.observer) {
        this.observer.disconnect();
      }

      const options = {
        root: container,
        rootMargin: '-20% 0px -20% 0px',
        threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1.0]
      };

      this.observer = new IntersectionObserver((entries) => {
        if (container.scrollTop < 50) {
          this.activeSection = 0;
          return;
        }

        let mostVisibleSection = null;
        let maxVisibility = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxVisibility) {
            maxVisibility = entry.intersectionRatio;
            mostVisibleSection = parseInt(entry.target.dataset.index, 10);
          }
        });

        if (mostVisibleSection !== null && maxVisibility > 0.3) {
          this.activeSection = mostVisibleSection;
        }
      }, options);

      for (let i = 0; i <= 6; i++) {
        const el = document.getElementById(`section-${i}`);
        if (el) {
          el.dataset.index = i;
          this.observer.observe(el);
        }
      }
    },

    calculateSectionOffsets() {
      this.sectionOffsets = [];
      const container = this.$refs.rightPanel;
      if (!container) return;

      for (let i = 0; i < 7; i++) {
        const el = document.getElementById(`section-${i}`);
        if (el) {
          this.sectionOffsets.push(el.offsetTop);
        }
      }
    },

    saveAll() {
      this.autoSave = true;
      this.$nextTick(() => {
        this.autoSave = false;
      });
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);

    this.$nextTick(() => {
      this.observeSections();
      
      if (this.$refs.rightPanel) {
        this.$refs.rightPanel.addEventListener("scroll", this.handleScroll, { passive: true });
      }
    });

    document.addEventListener("click", (event) => {
      const sidebar = document.querySelector(".candidate-left-panel");
      const button = document.querySelector(".btn-primary.d-md-none");

      if (
        this.isSideBarOpen &&
        sidebar &&
        !sidebar.contains(event.target) &&
        button &&
        !button.contains(event.target)
      ) {
        this.closeSidebar();
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    const scrollContainer = this.$refs.rightPanel;
    if (scrollContainer) {
      scrollContainer.removeEventListener("scroll", this.handleScroll);
    }

    document.removeEventListener("click", this.closeSidebar);

    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>

<style lang="scss" scoped>
.candidate-profile {
  display: flex;
  flex-direction: column;

  .main-content-card {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    min-height: 500px;
  }

  .profile-section {
    padding: 0 15px;
    min-height: 200px; 
  }

  .btn {
    min-width: 120px;
  }

  .tooltip-inner {
    color: #fff !important;
  }

  .custom-row {
    border-top: 1px solid #ccc;
    padding: 1rem 0 5rem;
    text-align: center;
    margin: 3rem 3rem 4rem;
  }

  .candidate-header {
    position: sticky;
    top: 0;
    z-index: 1000;
    background-color: #fff;
    padding: 0.5rem;
  }

  .candidate-content-container {
    flex: 1;
    display: flex;
    height: calc(100vh - 70px);
    overflow: hidden;
  }

  .candidate-left-panel {
    width: 300px;
    flex-shrink: 0;
    overflow-y: visible;
    height: calc(100vh - 70px);
  }

  .sticky-left-panel {
    position: relative;
    top: 0;
    height: calc(100vh - 100px);
    overflow-y: auto;
  }

  .candidate-right-panel {
    flex: 1;
    overflow-y: auto;
    height: calc(100vh - 70px);
    position: relative;
    scroll-behavior: smooth;
  }

  .save-all-button-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;

    .btn {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    }
  }

  .mobile-sidebar-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1040;
    display: none;
  }

  .bg-gradient-selected {
    background: linear-gradient(249.32deg,
        #00dfff -33.55%,
        #4c34e9 51.72%,
        #ff00ff 138.57%);
  }

  @media (max-width: 768px) {
    .btn {
      width: 100%;
      margin-bottom: 10px;
    }

    .d-flex.justify-content-between {
      flex-direction: column;

      >* {
        width: 100%;
        margin-bottom: 10px;
      }
    }

    .mobile-sidebar-backdrop {
      display: block;
    }

    .candidate-left-panel {
      position: fixed;
      top: 0;
      left: -300px;
      width: 280px;
      height: 100vh;
      z-index: 1050;
      box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
      overflow-y: auto;
      transition: transform 0.3s ease;

      &.mobile-open {
        left: 0;
      }
    }

    .candidate-content-container {
      flex-direction: column;
    }

    .candidate-right-panel {
      height: calc(100vh - 70px);
      overflow-y: auto;
      padding-top: 15px;
    }

    .save-all-button-container {
      right: 15px;
      bottom: 15px;
    }
  }
}

.button_1 {
  background: linear-gradient(249.32deg, #00DFFF -33.55%, #4C34E9 51.72%, #FF00FF 138.57%) !important;
  color: #fff !important;
  transition: background 0.3s ease, transform 0.2s ease;
}

.button_1:hover {
  background: linear-gradient(249.32deg, #FF00FF -33.55%, #4C34E9 51.72%, #00DFFF 138.57%) !important;
  transform: scale(1.02);
}
</style>